<template>
  <div class="h-screen w-screen bg-gray-50 flex flex-col md:flex-row overflow-hidden text-gray-800">
    
    <main class="flex-1 bg-white border-r border-gray-200 overflow-hidden flex flex-col h-full">
      <section class="flex-1 flex flex-col justify-center items-center h-full">

        <div v-if="loading" class="text-gray-500 font-medium animate-pulse">
          Cargando archivo...
        </div>
        
        <div v-else-if="error" class="text-red-600 font-medium text-center">
          {{ error }}
        </div>
        
        <div v-else-if="selectedEvidence" class="w-full h-full">
          <FileViewer
            :file-id="selectedEvidence.id"
            :visible="true"
            :inline="true"
            :hide-projection="true"
            :hide-delete="true"
            @close="goBack"
            @delete="handleDelete"
          />
        </div>

        <div v-else class="text-gray-400 text-center max-w-sm">
          <i class="fas fa-file-pdf text-4xl mb-3 text-gray-300"></i>
          <p>Selecciona una evidencia para abrirla en revisión.</p>
        </div>
      </section>
    </main>

    <aside class="w-full md:w-[400px] h-full overflow-y-auto shrink-0 bg-gray-50 border-l border-gray-200 p-4 flex flex-col gap-6">

      <div class="flex items-start gap-4 bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
        <div class="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 shrink-0 text-xl">
          <i class="fas fa-folder-open"></i>
        </div>
        <div>
          <h1 class="text-xl font-bold text-gray-900 tracking-tight">Centro de revisión</h1>
          <p class="text-xs text-gray-500 mt-1 leading-relaxed">
            Gestión, seguimiento y validación de evidencias
          </p>
        </div>
      </div>

      <section class="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <template v-if="selectedEvidence">
          
          <div class="space-y-3.5 border-b border-gray-100 pb-5 mb-5 text-xs">
  
  <div class="flex flex-col gap-0.5 pb-2 border-b border-gray-50">
    <span class="text-gray-400 font-normal">Evidencia</span>
    <span class="text-sm font-medium text-gray-900 break-words">
      {{ selectedEvidence.name || "Sin nombre" }}
    </span>
  </div>

  <div class="flex items-center justify-between py-0.5">
    <span class="text-gray-400 font-normal">Estado</span>
    <span class="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium" :class="statusTone(selectedEvidence)">
      {{ statusLabel(selectedEvidence) }}
    </span>
  </div>

  <div class="flex items-center justify-between py-0.5">
    <span class="text-gray-400 font-normal">Fecha</span>
    <span class="text-gray-700 font-medium">
      {{ formatNoteDate(selectedEvidence.createdAt) }}
    </span>
  </div>

  <div class="flex items-center justify-between py-0.5">
    <span class="text-gray-400 font-normal">Responsable</span>
    <span class="text-gray-700 font-medium truncate max-w-[200px]" :title="uploaderText(selectedEvidence)">
      {{ uploaderText(selectedEvidence) }}
    </span>
  </div>

</div>
          <div>
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2 text-gray-900 font-semibold text-sm">
                <i class="fas fa-clock-rotate-left text-gray-400"></i>
                <h3>Historial de revisión</h3>
              </div>
              <span v-if="notesList(selectedEvidence).length" class="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-indigo-600 bg-indigo-50 rounded-full">
                {{ notesList(selectedEvidence).length }}
              </span>
            </div>

            <div v-if="notesList(selectedEvidence).length" class="relative pl-4 border-l-2 border-gray-100 space-y-4">
              <article
                v-for="(note, index) in notesList(selectedEvidence)"
                :key="`${index}-${note.createdAt || note.requestedBy || 'note'}`"
                class="relative"
              >
                <div class="absolute -left-[21px] top-1.5 bg-white h-2.5 w-2.5 rounded-full border-2 border-indigo-500"></div>

                <div class="bg-gray-50 rounded-lg border border-gray-100 transition-all duration-200">
                  <div class="flex items-center justify-between p-3 cursor-pointer select-none" @click="toggleNote(index)">
                    <div class="flex flex-col text-xs">
                      <strong class="text-gray-900 font-semibold">{{ note.requestedBy || "Usuario" }}</strong>
                      <span class="text-gray-400 mt-0.5">{{ formatNoteDate(note.createdAt) }}</span>
                    </div>
                    <i class="fas text-gray-400 text-xs transition-transform duration-200" :class="expandedNotes[index] ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  </div>

                  <div v-if="expandedNotes[index]" class="px-3 pb-3 pt-1 border-t border-gray-100 text-sm text-gray-700">
                    <div class="bg-white p-2.5 rounded border border-gray-200/60 shadow-inner text-xs leading-relaxed">
                      {{ note.message || note.text || "Solicitó más información" }}
                    </div>

                    <div v-if="note.responses && note.responses.length" class="mt-3 pl-3 border-l-2 border-gray-200 space-y-2">
                      <div
                        v-for="(response, responseIndex) in note.responses"
                        :key="`${index}-response-${responseIndex}`"
                        class="bg-white p-2 rounded border border-gray-100 shadow-sm"
                      >
                        <div class="flex items-center gap-1.5 text-[11px] text-gray-500 mb-1">
                          <i class="fas fa-reply fa-flip-horizontal text-gray-300"></i>
                          <strong class="text-gray-700">{{ response.responder || "Usuario" }}</strong>
                          <span>•</span>
                          <span>{{ formatNoteDate(response.createdAt) }}</span>
                        </div>
                        <p class="text-xs text-gray-600 pl-4">
                          {{ response.message || response.text || "Sin respuesta" }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </div>

            <div v-else class="text-center py-6 px-4 border border-dashed border-gray-200 rounded-xl bg-gray-50/50">
              <i class="fas fa-inbox text-2xl text-gray-300 mb-2 block"></i>
              <h4 class="text-xs font-semibold text-gray-700">Sin actividad registrada</h4>
              <p class="text-[11px] text-gray-400 mt-1 max-w-[240px] mx-auto leading-normal">
                Las observaciones aparecerán aquí cuando exista interacción sobre esta evidencia.
              </p>
            </div>
          </div>

        </template>

        <div v-else class="text-center py-10 px-4">
          <i class="fas fa-file-circle-question text-4xl text-gray-300 mb-3 block"></i>
          <h4 class="text-sm font-semibold text-gray-700">Ninguna evidencia seleccionada</h4>
          <p class="text-xs text-gray-400 mt-1 max-w-[240px] mx-auto leading-normal">
            Selecciona una evidencia para visualizar su información y seguimiento.
          </p>
        </div>
      </section>

      <section class="flex flex-col gap-3 mt-auto">
        <button
          class="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-indigo-500"
          :disabled="!selectedEvidence || savingNote"
          @click="requestMoreInfo"
        >
          <i class="fas fa-comment-dots text-xs"></i>
          Solicitar aclaración
        </button>

        <button
          class="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all border shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none bg-white hover:bg-gray-50 text-gray-700 border-gray-300 focus:ring-indigo-500"
          :disabled="!selectedEvidence"
          @click="downloadSelected"
        >
          <i class="fas fa-download text-xs"></i>
          Descargar archivo
        </button>
      </section>

    </aside>
  </div>
</template>

<script>
import { addDoc, arrayUnion, collection, doc, getDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "@/fb";
import { getEvidenceSelection, clearEvidenceSelection } from "@/evidenceReviewContext";
import FileViewer from "@/components/FileViewer.vue";
import axios from "axios";

const DOC_TYPES = ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx"];
const MEDIA_TYPES = ["mp3", "wav", "m4a", "ogg", "mp4", "mov", "webm", "mkv"];
const IMAGE_TYPES = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "tiff"];

export default {
  name: "EvidenceManagerPage",
  components: { FileViewer },
  data() {
    return {
      loading: false,
      savingNote: false,
      error: "",
      selectedEvidence: null,
      resolvedFileUrl: "",
      officeViewerUrl: "",
      viewerType: "",
      viewerLabel: "Sin archivo",
      isVideo: false,
      plyrInstance: null,
      lightbox: null,
      replyDrafts: {},
      expandedNotes: {},
    };
  },
  created() {
    this.loadSelectedEvidence();
  },
  methods: {
    labelsText(docItem) {
      if (!docItem || !docItem.labels) {
        return "Sin etiquetas";
      }

      if (Array.isArray(docItem.labels)) {
        return docItem.labels.join(", ");
      }

      return String(docItem.labels);
    },
    uploaderText(docItem) {
      if (!docItem || !docItem.uploader) {
        return "No disponible";
      }

      return docItem.uploader.name || docItem.uploader.type || "No disponible";
    },
    notesList(docItem) {
      if (!docItem || !Array.isArray(docItem.notes)) {
        return [];
      }

      return docItem.notes;
    },
    noteAuthorName() {
      const delegateData = this.$delegateStore?.state?.delegateData || {};

      return (
        delegateData.name ||
        [delegateData.firstName, delegateData.lastName].filter(Boolean).join(" ") ||
        delegateData.email ||
        this.$delegateStore?.state?.authContext?.delegateId ||
        "Usuario"
      );
    },
    formatNoteDate(value) {
  if (!value) {
    return "Sin fecha";
  }

  if (typeof value === "string") {
    const parsed = new Date(value);

    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleString("es-CO");
    }

    return value;
  }

  if (typeof value === "number") {
    return new Date(value).toLocaleString("es-CO");
  }

  if (value.seconds !== undefined && value.nanoseconds !== undefined) {
    return new Date(value.seconds * 1000).toLocaleString("es-CO");
  }

  if (value.toDate && typeof value.toDate === "function") {
    return value.toDate().toLocaleString("es-CO");
  }

  return "Sin fecha";
},
    statusLabel(docItem) {
      return (docItem && (docItem.status || docItem.reviewStatus || docItem.state)) || "Pendiente";
    },
    statusTone(docItem) {
      const status = String(this.statusLabel(docItem)).toLowerCase();

      if (status.includes("aprob")) {
        return "pill-approved";
      }

      if (status.includes("rech")) {
        return "pill-rejected";
      }

      if (status.includes("revis")) {
        return "pill-review";
      }

      if (status.includes("comprob")) {
        return "pill-unprovable";
      }

      return "pill-pending";
    },
    async resolveDocumentUrl(fileKey) {
      if (!fileKey) {
        throw new Error("Missing fileKey");
      }

      const response = await fetch(this.buildApiDocumentUrl(fileKey));
      if (!response.ok) {
        throw new Error("Unable to resolve file URL");
      }

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const payload = await response.json();
        return payload.url || payload.data?.url || payload.fileUrl || payload.link || "";
      }

      const text = await response.text();
      return text.trim();
    },
    buildApiDocumentUrl(fileKey) {
      const normalizedFileKey = String(fileKey || "").replace(/^\/+/, "");
      return `https://api.jgmun.com/api/docs/${encodeURI(normalizedFileKey)}`;
    },
    getMediaDocRef(mediaId, authContext) {
      return doc(
        db,
        "commissions",
        authContext.cohort,
        "commissions",
        authContext.commissionId,
        "media",
        mediaId
      );
    },
    async loadSelectedEvidence() {
      this.loading = true;
      this.error = "";

      try {
        let selected = getEvidenceSelection();

        if (!selected && this.$route.query.evidenceId) {
          selected = { id: this.$route.query.evidenceId };
        }

        if (!selected) {
          this.selectedEvidence = null;
          return;
        }

        if (!this.$delegateStore.state.loaded) {
          await this.$loadDelegateProfile();
        }

        const authContext = this.$delegateStore.state.authContext || {};
        const mediaRef = this.getMediaDocRef(selected.id, authContext);
        const mediaSnapshot = await getDoc(mediaRef);
        const mediaData = mediaSnapshot.exists() ? mediaSnapshot.data() : {};

        const currentDelegationId = this.$delegateStore.state.delegateData?.delegation?.id;
        const isOwner = mediaData.uploader?.id === currentDelegationId;
        const isPublic = !!mediaData.sharedWithDelegates;
        const isSpecificallyShared = Array.isArray(mediaData.sharedDelegations) && mediaData.sharedDelegations.includes(currentDelegationId);

        if (!isOwner && !isPublic && !isSpecificallyShared) {
          this.error = "No tienes permisos para ver esta revisión detallada.";
          this.selectedEvidence = null;
          return;
        }

        this.selectedEvidence = {
          ...selected,
          ...mediaData,
          id: selected.id,
        };
      } catch (error) {
        this.error = "No se pudo cargar la evidencia seleccionada.";
      } finally {
        this.loading = false;
      }
    },
    async handleDelete(file) {
      if (!file) return;
      if (!confirm('¿Estás seguro de eliminar este archivo permanentemente?')) return;

      try {
        if (file.fileKey) {
          await axios.delete(`https://api.jgmun.com/api/docs/${encodeURIComponent(file.fileKey)}`);
        }

        const authContext = this.$delegateStore.state.authContext || {};
        await deleteDoc(doc(db, `commissions/${authContext.cohort}/commissions/${authContext.commissionId}/media`, file.id));
        
        this.goBack();
      } catch (err) {
        this.error = "No se pudo eliminar el archivo.";
      }
    },
    async sendNotification(message) {
      const uploaderId = this.selectedEvidence?.uploader?.id;
      if (!uploaderId) return;

      const myId = this.$delegateStore?.state?.delegateData?.delegation?.id;
      if (uploaderId === myId) return; // No notificarse a sí mismo

      const authContext = this.$delegateStore.state.authContext || {};
      const notificationsRef = collection(db, "notifications");

      try {
        await addDoc(notificationsRef, {
          recipientId: uploaderId,
          senderName: this.noteAuthorName(),
          message,
          evidenceId: this.selectedEvidence.id,
          createdAt: new Date().toISOString(),
          read: false,
          cohort: authContext.cohort,
          commissionId: authContext.commissionId
        });
      } catch (e) {
        // Error silenciado para producción
      }
    },
    toggleNote(index) {
      this.$set(this.expandedNotes, index, !this.expandedNotes[index]);
    },
    async requestMoreInfo() {
      if (!this.selectedEvidence || this.savingNote) {
        return;
      }

      this.savingNote = true;

      try {
        if (!this.$delegateStore.state.loaded) {
          await this.$loadDelegateProfile();
        }

        const authContext = this.$delegateStore.state.authContext || {};
        const mediaRef = this.getMediaDocRef(this.selectedEvidence.id, authContext);
        const note = {
          action: "request_more_info",
          message: "Solicitó más información",
          requestedBy: this.noteAuthorName(),
          requestedById: authContext.delegateId || "",
          createdAt: new Date().toISOString(),
        };

        await updateDoc(mediaRef, {
          notes: arrayUnion(note),
        });

        this.selectedEvidence = {
          ...this.selectedEvidence,
          notes: [...this.notesList(this.selectedEvidence), note],
        };

        await this.sendNotification(`Se ha solicitado más información sobre tu evidencia: "${this.selectedEvidence.name}"`);
      } catch (error) {
        this.error = "No se pudo registrar la nota en la evidencia seleccionada.";
      } finally {
        this.savingNote = false;
      }
    },
    async replyToNote(index) {
      if (!this.selectedEvidence || this.savingNote) {
        return;
      }

      const draft = (this.replyDrafts[index] || "").trim();
      if (!draft) {
        return;
      }

      this.savingNote = true;

      try {
        const notes = this.notesList(this.selectedEvidence).map((note) => ({
          ...note,
          responses: Array.isArray(note.responses) ? [...note.responses] : [],
        }));

        if (!notes[index]) return;

        notes[index].responses.push({
          responder: this.noteAuthorName(),
          responderId: this.$delegateStore?.state?.authContext?.delegateId || "",
          message: draft,
          createdAt: new Date().toISOString(),
        });

        const authContext = this.$delegateStore.state.authContext || {};
        const mediaRef = this.getMediaDocRef(this.selectedEvidence.id, authContext);
        await updateDoc(mediaRef, { notes });
        this.selectedEvidence = { ...this.selectedEvidence, notes };
        await this.sendNotification(`Nueva respuesta en tu evidencia: "${this.selectedEvidence.name}"`);
        this.$set(this.replyDrafts, index, "");
      } catch (error) {
        this.error = "No se pudo responder la nota seleccionada.";
      } finally {
        this.savingNote = false;
      }
    },
    downloadSelected() {
      if (!this.selectedEvidence) {
        return;
      }

      const fileKey = this.selectedEvidence.fileKey;
      if (!fileKey) {
        return;
      }

      this.resolveDocumentUrl(fileKey)
        .then((fileUrl) => {
          const link = document.createElement("a");
          link.href = fileUrl;
          link.target = "_blank";
          link.rel = "noopener";
          link.download = this.selectedEvidence.name || "evidence-file";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(() => {
          this.error = "No se pudo descargar el archivo seleccionado.";
        });
    },
    goBack() {
      clearEvidenceSelection();
      this.$router.push("/evidence");
    },
  },
};
</script>